import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Printer, RefreshCcw, Sparkles, Building2,
  CheckCircle2, XCircle, Clock, Users,
} from "lucide-react";

const STATUS_LABELS = {
  scheduled: "Scheduled", checked_in: "Checked In", in_progress: "In Progress",
  completed: "Completed", no_show: "No-Show", cancelled: "Cancelled",
  rescheduled: "Rescheduled",
};
const STATUS_COLORS = {
  scheduled: "bg-zinc-100 text-zinc-700", checked_in: "bg-blue-100 text-blue-800",
  in_progress: "bg-amber-100 text-amber-800", completed: "bg-emerald-100 text-emerald-800",
  no_show: "bg-red-100 text-red-800", cancelled: "bg-zinc-200 text-zinc-600",
  rescheduled: "bg-purple-100 text-purple-800",
};
const OUTCOME_LABELS = {
  follow_up_requested: "Follow-up requested",
  referral_offered: "Internal referral / introduction offered",
  capability_review_requested: "Capability review requested",
  opportunity_identified: "Potential opportunity identified",
  vendor_registration_assistance: "Vendor-registration assistance needed",
  no_current_fit: "No current opportunity or fit",
  agreed_next_step: "Agreed next step",
};

const pct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`;
const fmtTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/New_York" }); }
  catch { return iso; }
};

function Kpi({ label, value, sub, testid }) {
  return (
    <div className="card-flat p-5" data-testid={testid}>
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">{label}</div>
      <div className="serif text-4xl mt-2">{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }) {
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${STATUS_COLORS[status] || "bg-zinc-100"}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function ProgramTracker() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [dash, setDash] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [rels, setRels] = useState([]);
  const [stages, setStages] = useState([]);
  const [closedStatuses, setClosedStatuses] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [outcomeFor, setOutcomeFor] = useState(null);
  const [blockFor, setBlockFor] = useState(null);
  const [relFor, setRelFor] = useState(null);
  const [recon, setRecon] = useState(null);
  const [viewerMgrOpen, setViewerMgrOpen] = useState(false);

  const role = user?.program_role || (["admin", "tl_internal"].includes(user?.role) ? "admin" : null);
  const canEdit = role === "admin" || role === "staff";
  const isAdmin = role === "admin";

  useEffect(() => {
    if (loading) return;
    const allowed = ["admin", "tl_internal"].includes(user?.role) ||
      ["admin", "staff", "viewer"].includes(user?.program_role);
    if (!user || !allowed) { nav("/"); return; }
    load();
    // eslint-disable-next-line
  }, [loading, user]);

  const load = async () => {
    try {
      const reqs = [
        api.get("/program/dashboard"),
        api.get("/program/engagements"),
        api.get("/program/showcase-blocks"),
        api.get("/program/relationships"),
        api.get("/program/reconciliation"),
      ];
      const [d, e, b, r, rec] = await Promise.all(reqs);
      setDash(d.data);
      setEngagements(e.data.engagements);
      setBlocks(b.data.blocks);
      setRels(r.data.relationships);
      setStages(r.data.stages);
      setClosedStatuses(r.data.closed_statuses);
      setRecon(rec.data);
    } catch (err) {
      toast.error("Could not load program data.");
    }
  };

  const setStatus = async (eng, status) => {
    try {
      await api.post(`/program/engagements/${eng.kind}/${eng.id}/status`, {
        status, expected_updated_at: eng.event_status_updated_at || null,
      });
      toast.success(`Marked ${STATUS_LABELS[status]}`);
      await load();
    } catch (err) {
      if (err?.response?.status === 409) {
        const dt = err.response.data?.detail;
        toast.error(typeof dt === "object" ? dt.message : "Meeting changed — reloading.");
        await load();
      } else {
        toast.error(err?.response?.data?.detail || "Update failed");
      }
    }
  };

  const downloadExcel = async () => {
    try {
      setBusy(true);
      const res = await api.get("/program/report.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "tl-summit-program-report.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
    finally { setBusy(false); }
  };

  const runSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/program/sync-all");
      const rec = data.reconciliation;
      toast.success(`Sync complete · ${rec.formal_meetings} formal meetings · ${rec.showcase_blocks} showcase blocks · ${rec.relationships} relationships`);
      await load();
    } catch { toast.error("Sync failed"); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => engagements.filter((e) =>
    (filterType === "all" || e.kind === filterType) &&
    (filterStatus === "all" || e.event_status === filterStatus)
  ), [engagements, filterType, filterStatus]);

  if (loading || !dash) {
    return <Layout><div className="max-w-7xl mx-auto px-6 py-20 text-zinc-500">Loading program tracker…</div></Layout>;
  }

  const ec = dash.engagement_counts;
  const TABS = [
    ["dashboard", "Dashboard"], ["meetings", "Meetings"],
    ["showcase", "Showcase Blocks"], ["relationships", "Relationships"],
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="program-tracker">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.16em] text-zinc-500 hover:text-black" data-testid="program-back">
              <ArrowLeft size={14} /> Admin
            </Link>
            <h1 className="serif text-3xl sm:text-4xl mt-2">Program Tracker</h1>
            <p className="text-sm text-zinc-500 mt-1">
              Supplier-development outcomes · {role === "viewer" ? "Read-only (T+L leadership)" : role === "staff" ? "Event staff" : "Administrator"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <button onClick={() => setViewerMgrOpen(true)} className="btn-outline-black h-10 px-4 text-xs inline-flex items-center gap-2" data-testid="program-viewers-btn">
                <Users size={14} /> Report viewers
              </button>
            )}
            {isAdmin && (
              <button onClick={runSync} disabled={busy} className="btn-outline-black h-10 px-4 text-xs inline-flex items-center gap-2" data-testid="program-sync-btn">
                <RefreshCcw size={14} /> Sync data
              </button>
            )}
            <button onClick={downloadExcel} disabled={busy} className="btn-outline-black h-10 px-4 text-xs inline-flex items-center gap-2" data-testid="program-export-excel">
              <Download size={14} /> Excel
            </button>
            <Link to="/admin/program/report" className="btn-outline-black h-10 px-4 text-xs inline-flex items-center gap-2" data-testid="program-print-report">
              <Printer size={14} /> Report / PDF
            </Link>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 border-b border-zinc-200 mb-6">
          {TABS.map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.16em] border-b-2 -mb-px ${tab === v ? "border-black text-black" : "border-transparent text-zinc-400 hover:text-zinc-700"}`}
              data-testid={`program-tab-${v}`}>{l}</button>
          ))}
        </div>

        {tab === "dashboard" && (
          <div className="space-y-8" data-testid="program-dashboard">
            {recon && (
              <div className="card-flat p-5" data-testid="program-reconciliation">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Reconciliation</div>
                  <div className="text-[11px] text-zinc-400">as of {new Date(recon.generated_at).toLocaleString("en-US", { timeZone: "America/New_York" })} ET</div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-3 text-sm">
                  <div><div className="serif text-2xl" data-testid="recon-formal">{recon.formal_meetings}</div><div className="text-[10px] uppercase text-zinc-400">Formal meetings</div></div>
                  <div><div className="serif text-2xl">{recon.by_type.sbe_prime_matchmaking}</div><div className="text-[10px] uppercase text-zinc-400">Matchmaking</div></div>
                  <div><div className="serif text-2xl">{recon.by_type.prime_tl_sourcing}</div><div className="text-[10px] uppercase text-zinc-400">T+L Sourcing</div></div>
                  <div><div className="serif text-2xl">{recon.showcase_blocks}</div><div className="text-[10px] uppercase text-zinc-400">Showcase blocks</div></div>
                  <div><div className="serif text-2xl">{recon.unique_prime_sbe_pairs}</div><div className="text-[10px] uppercase text-zinc-400">Unique pairs</div></div>
                  <div><div className="serif text-2xl">{recon.relationships}</div><div className="text-[10px] uppercase text-zinc-400">Relationships</div></div>
                </div>
                {(() => {
                  const a = recon.anomalies;
                  const issues = [];
                  if (a.skipped_matchmaking_no_counterpart?.length) issues.push(`${a.skipped_matchmaking_no_counterpart.length} skipped (no counterpart)`);
                  if (a.duplicate_meetings_same_pair) issues.push(`${a.duplicate_meetings_same_pair} duplicate meeting(s) same pair`);
                  if (a.duplicate_relationship_records?.length) issues.push(`${a.duplicate_relationship_records.length} duplicate relationship(s)`);
                  if (a.orphan_relationships?.length) issues.push(`${a.orphan_relationships.length} orphan relationship(s)`);
                  if (a.pairs_missing_relationship?.length) issues.push(`${a.pairs_missing_relationship.length} pair(s) missing relationship`);
                  if (a.duplicate_showcase_blocks?.length) issues.push(`${a.duplicate_showcase_blocks.length} duplicate showcase block(s)`);
                  return (
                    <div className={`mt-3 text-xs ${issues.length ? "text-amber-700" : "text-emerald-700"}`} data-testid="recon-anomalies">
                      {issues.length ? `⚠ ${issues.join(" · ")}` : "✓ No skipped, duplicate or invalid records. Relationships equal unique Prime–SBE pairs."}
                    </div>
                  );
                })()}
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Scheduled engagements</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Kpi testid="kpi-mm" label="SBE–Prime Matchmaking" value={ec.matchmaking_meetings} sub="one-on-one meetings" />
                <Kpi testid="kpi-tl" label="Prime–T+L Sourcing" value={ec.tl_sourcing_meetings} sub="sourcing meetings" />
                <Kpi testid="kpi-total" label="Total Formal Meetings" value={ec.total_formal_meetings} sub="matchmaking + sourcing" />
                <Kpi testid="kpi-blocks" label="Showcase Station Blocks" value={ec.showcase_blocks} sub="reported separately" />
                <Kpi testid="kpi-hours" label="Showcase Station-Hours" value={ec.showcase_station_hours} sub="1:00–4:00 PM · not meetings" />
              </div>
              <p className="text-xs text-zinc-500 mt-3">
                Showcase Station Blocks are company-level participation windows and are <strong>not</strong> counted as one-on-one meetings.
                The formal total is {ec.total_formal_meetings} meetings; Showcase blocks are reported separately.
              </p>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Completion & attendance (formal meetings)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                <Kpi testid="kpi-completed" label="Completed" value={dash.status.completed} />
                <Kpi testid="kpi-completion-rate" label="Completion Rate" value={pct(dash.rates.completion_rate)} sub={`of ${dash.rates.denominator}`} />
                <Kpi testid="kpi-noshow-rate" label="No-Show Rate" value={pct(dash.rates.no_show_rate)} />
                <Kpi testid="kpi-checkedin" label="Checked In" value={dash.status.checked_in} />
                <Kpi testid="kpi-unique-sbe" label="Unique Prime–SBE" value={dash.connections.unique_prime_sbe} />
                <Kpi testid="kpi-unique-tl" label="Unique Prime–T+L" value={dash.connections.unique_prime_tl} />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Outcomes</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Kpi testid="kpi-followups" label="Follow-ups Requested" value={dash.outcomes.follow_ups_requested} />
                <Kpi testid="kpi-referrals" label="Referrals Offered" value={dash.outcomes.referrals_offered} />
                <Kpi testid="kpi-opps" label="Opportunities" value={dash.outcomes.opportunities_identified} />
                <Kpi testid="kpi-capreview" label="Capability Reviews" value={dash.outcomes.capability_reviews_requested} />
                <Kpi testid="kpi-vendorreg" label="Vendor-Reg Assist" value={dash.outcomes.vendor_registration_assistance} />
                <Kpi testid="kpi-nofit" label="No Current Fit" value={dash.outcomes.no_current_fit} />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Showcase performance (separate from meetings)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <Kpi testid="kpi-sc-blocks" label="Station Blocks" value={dash.showcase.blocks} />
                <Kpi testid="kpi-sc-hours" label="Station-Hours" value={dash.showcase.station_hours} />
                <Kpi testid="kpi-sc-active" label="Stations Opened" value={dash.showcase.stations_active} />
                <Kpi testid="kpi-sc-convos" label="Conversations" value={dash.showcase.conversations} />
                <Kpi testid="kpi-sc-leads" label="Leads / Follow-ups" value={`${dash.showcase.leads} / ${dash.showcase.follow_up_requests}`} />
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-3">Relationship funnel</div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <Kpi testid="kpi-rel-total" label="Relationships" value={dash.relationship_funnel.total} />
                <Kpi testid="kpi-rel-intro" label="Intros Completed" value={dash.relationship_funnel.introductions_completed} />
                <Kpi testid="kpi-rel-follow" label="With Follow-up" value={dash.relationship_funnel.with_follow_up} />
                <Kpi testid="kpi-rel-qual" label="Qualified Opps" value={dash.relationship_funnel.qualified_opportunities} />
                <Kpi testid="kpi-rel-agree" label="Agreements+" value={dash.relationship_funnel.agreements_or_contracts} />
                <Kpi testid="kpi-rel-spend" label="Active Tier 2" value={dash.relationship_funnel.active_tier2_spend} />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <Breakdown title="By meeting type" rows={dash.by_type} />
              <Breakdown title="By business category" rows={dash.by_category} />
              <Breakdown title="By Prime" rows={dash.by_prime} />
              <Breakdown title="By SBE" rows={dash.by_sbe} />
            </div>
          </div>
        )}

        {tab === "meetings" && (
          <div data-testid="program-meetings">
            <div className="flex flex-wrap gap-3 mb-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-56" data-testid="meetings-filter-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All meeting types</SelectItem>
                  <SelectItem value="matchmaking">SBE–Prime Matchmaking</SelectItem>
                  <SelectItem value="tl">Prime–T+L Sourcing</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-48" data-testid="meetings-filter-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase tracking-[0.12em] text-zinc-500">
                  <tr>
                    <th className="text-left p-3">Time</th>
                    <th className="text-left p-3">Type</th>
                    <th className="text-left p-3">Location</th>
                    <th className="text-left p-3">Prime</th>
                    <th className="text-left p-3">With</th>
                    <th className="text-left p-3">Status</th>
                    <th className="text-left p-3">Outcome</th>
                    {canEdit && <th className="text-left p-3">Actions</th>}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((e) => (
                    <tr key={`${e.kind}-${e.id}`} className="border-t border-zinc-100" data-testid={`meeting-row-${e.id}`}>
                      <td className="p-3 whitespace-nowrap">{fmtTime(e.start_iso)}</td>
                      <td className="p-3 whitespace-nowrap">{e.kind === "tl" ? "T+L Sourcing" : "Matchmaking"}</td>
                      <td className="p-3 whitespace-nowrap">{e.location}</td>
                      <td className="p-3">{e.prime_company}</td>
                      <td className="p-3">{e.kind === "tl" ? `${e.tl_leader_name || "T+L"} · T+L` : e.sbe_company}</td>
                      <td className="p-3">
                        {canEdit ? (
                          <Select value={e.event_status} onValueChange={(v) => setStatus(e, v)}>
                            <SelectTrigger className="w-36 h-8 text-xs" data-testid={`meeting-status-${e.id}`}><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : <StatusBadge status={e.event_status} />}
                      </td>
                      <td className="p-3 text-xs text-zinc-600 max-w-[220px]">
                        {(e.outcome?.types || []).map((t) => OUTCOME_LABELS[t]).join(", ") || <span className="text-zinc-300">—</span>}
                      </td>
                      {canEdit && (
                        <td className="p-3">
                          <button onClick={() => setOutcomeFor(e)} className="text-xs underline hover:no-underline" data-testid={`meeting-outcome-btn-${e.id}`}>Outcome</button>
                        </td>
                      )}
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={canEdit ? 8 : 7} className="p-8 text-center text-zinc-400">No meetings match these filters.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "showcase" && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="program-showcase">
            {blocks.map((b) => (
              <div key={b.id} className="card-flat p-5" data-testid={`showcase-card-${b.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-[0.16em] text-zinc-500">Station {b.station_number}</div>
                  <span className={`text-xs px-2 py-1 rounded ${b.station_opened ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100 text-zinc-500"}`}>
                    {b.station_opened === null ? "Not recorded" : b.station_opened ? "Opened" : "Not opened"}
                  </span>
                </div>
                <div className="serif text-xl mt-2 flex items-center gap-2"><Building2 size={16} /> {b.prime_company}</div>
                <div className="text-xs text-zinc-500 mt-1">1:00 – 4:00 PM ET · 3 station-hours</div>
                <div className="grid grid-cols-3 gap-2 mt-4 text-center">
                  <div><div className="serif text-2xl">{b.conversations_count || 0}</div><div className="text-[10px] uppercase text-zinc-400">Convos</div></div>
                  <div><div className="serif text-2xl">{b.leads_count || 0}</div><div className="text-[10px] uppercase text-zinc-400">Leads</div></div>
                  <div><div className="serif text-2xl">{b.follow_up_requests_count || 0}</div><div className="text-[10px] uppercase text-zinc-400">Follow-ups</div></div>
                </div>
                {b.onsite_reps?.length > 0 && (
                  <div className="text-xs text-zinc-500 mt-3 flex items-center gap-1"><Users size={12} /> {b.onsite_reps.join(", ")}</div>
                )}
                {canEdit && (
                  <button onClick={() => setBlockFor(b)} className="btn-outline-black h-8 px-3 text-xs mt-4 w-full" data-testid={`showcase-edit-${b.id}`}>Record station activity</button>
                )}
              </div>
            ))}
          </div>
        )}

        {tab === "relationships" && (
          <div className="card-flat overflow-x-auto" data-testid="program-relationships">
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase tracking-[0.12em] text-zinc-500">
                <tr>
                  <th className="text-left p-3">Prime</th>
                  <th className="text-left p-3">SBE</th>
                  <th className="text-left p-3">Stage</th>
                  <th className="text-left p-3">Owner</th>
                  <th className="text-left p-3">Next action</th>
                  <th className="text-left p-3">Due</th>
                  {canEdit && <th className="text-left p-3"></th>}
                </tr>
              </thead>
              <tbody>
                {rels.map((r) => (
                  <tr key={r.id} className="border-t border-zinc-100" data-testid={`rel-row-${r.id}`}>
                    <td className="p-3">{r.prime_company}</td>
                    <td className="p-3">{r.sbe_company}</td>
                    <td className="p-3"><span className="text-xs px-2 py-1 rounded bg-zinc-100">{r.stage}</span></td>
                    <td className="p-3">{r.owner || <span className="text-zinc-300">—</span>}</td>
                    <td className="p-3 max-w-[240px] text-xs text-zinc-600">{r.next_action || <span className="text-zinc-300">—</span>}</td>
                    <td className="p-3 whitespace-nowrap text-xs">{r.follow_up_due || "—"}</td>
                    {canEdit && <td className="p-3"><button onClick={() => setRelFor(r)} className="text-xs underline hover:no-underline" data-testid={`rel-edit-${r.id}`}>Edit</button></td>}
                  </tr>
                ))}
                {rels.length === 0 && (
                  <tr><td colSpan={canEdit ? 7 : 6} className="p-8 text-center text-zinc-400">No relationships yet — they auto-create when matchmaking meetings are published.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <OutcomeDialog eng={outcomeFor} onClose={() => setOutcomeFor(null)} onSaved={load} />
      <ShowcaseDialog block={blockFor} onClose={() => setBlockFor(null)} onSaved={load} />
      <RelationshipDialog rel={relFor} stages={stages} closedStatuses={closedStatuses} onClose={() => setRelFor(null)} onSaved={load} />
      <ReportViewerManager open={viewerMgrOpen} onClose={() => setViewerMgrOpen(false)} />
    </Layout>
  );
}

function ReportViewerManager({ open, onClose }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/program/report-viewers");
      setList(data.tl_attendees || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (open) load(); }, [open]);

  const toggle = async (email, grant) => {
    setBusy(true);
    try {
      await api.post("/admin/program/report-viewers", { email, grant });
      toast.success(grant ? "Granted report access" : "Revoked report access");
      await load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="report-viewers-dialog">
        <DialogHeader><DialogTitle>Designated T+L report viewers</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Grant or revoke program-wide read-only access for T+L Team members.</DialogDescription>
        <p className="text-sm text-zinc-500 -mt-2">
          Only designated T+L Team members get program-wide read-only access to the dashboard and exports.
          Everyone else on the T+L team sees only their own schedule.
        </p>
        <div className="max-h-72 overflow-y-auto divide-y divide-zinc-100">
          {list.map((p) => (
            <div key={p.id} className="flex items-center justify-between py-2 text-sm" data-testid={`viewer-row-${p.id}`}>
              <div>
                <div className="font-medium">{p.name}</div>
                <div className="text-xs text-zinc-500">{p.email}</div>
              </div>
              <Switch checked={!!p.program_report_viewer}
                onCheckedChange={(v) => toggle(p.email, v)} disabled={busy}
                data-testid={`viewer-toggle-${p.id}`} />
            </div>
          ))}
          {list.length === 0 && <div className="py-6 text-center text-zinc-400 text-sm">No T+L Team members registered yet.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Breakdown({ title, rows }) {  return (
    <div className="card-flat p-5">
      <div className="text-xs uppercase tracking-[0.16em] text-zinc-500 mb-3">{title}</div>
      <table className="w-full text-sm">
        <thead className="text-[10px] uppercase text-zinc-400">
          <tr><th className="text-left pb-2">Name</th><th className="text-right pb-2">Total</th><th className="text-right pb-2">Done</th><th className="text-right pb-2">No-show</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-zinc-100">
              <td className="py-2">{r.name}</td>
              <td className="py-2 text-right">{r.total}</td>
              <td className="py-2 text-right text-emerald-700">{r.completed}</td>
              <td className="py-2 text-right text-red-700">{r.no_show}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-zinc-300">No data</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function OutcomeDialog({ eng, onClose, onSaved }) {
  const [types, setTypes] = useState([]);
  const [owner, setOwner] = useState("");
  const [due, setDue] = useState("");
  const [nextStep, setNextStep] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (eng) {
      const oc = eng.outcome || {};
      setTypes(oc.types || []);
      setOwner(oc.follow_up_owner || "");
      setDue(oc.follow_up_due || "");
      setNextStep(oc.agreed_next_step || "");
      setNotes(oc.notes || "");
    }
  }, [eng]);

  const toggle = (t) => setTypes((p) => p.includes(t) ? p.filter((x) => x !== t) : [...p, t]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/program/engagements/${eng.kind}/${eng.id}/outcome`, {
        types, follow_up_owner: owner || null, follow_up_due: due || null,
        agreed_next_step: nextStep || null, notes: notes || null,
      });
      toast.success("Outcome recorded");
      onClose(); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!eng} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="outcome-dialog">
        <DialogHeader><DialogTitle>Record meeting outcome</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Select outcomes and follow-up details for this meeting.</DialogDescription>
        {eng && <p className="text-sm text-zinc-500 -mt-2">{eng.prime_company} · {eng.kind === "tl" ? eng.tl_leader_name : eng.sbe_company}</p>}
        <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
          {Object.entries(OUTCOME_LABELS).map(([v, l]) => (
            <label key={v} className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={types.includes(v)} onChange={() => toggle(v)} data-testid={`outcome-type-${v}`} />
              {l}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Follow-up owner</Label><Input value={owner} onChange={(e) => setOwner(e.target.value)} data-testid="outcome-owner" /></div>
          <div><Label className="text-xs">Follow-up due</Label><Input type="date" value={due} onChange={(e) => setDue(e.target.value)} data-testid="outcome-due" /></div>
        </div>
        <div><Label className="text-xs">Agreed next step</Label><Input value={nextStep} onChange={(e) => setNextStep(e.target.value)} data-testid="outcome-nextstep" /></div>
        <div><Label className="text-xs">Notes</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="outcome-notes" /></div>
        <DialogFooter>
          <button onClick={save} disabled={busy} className="btn-outline-black h-10 px-5 text-xs" data-testid="outcome-save">Save outcome</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ShowcaseDialog({ block, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (block) setF({
      station_opened: !!block.station_opened, active: !!block.active,
      onsite_reps: (block.onsite_reps || []).join(", "),
      conversations_count: block.conversations_count || 0,
      leads_count: block.leads_count || 0,
      follow_up_requests_count: block.follow_up_requests_count || 0,
      notes: block.notes || "",
    });
  }, [block]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/program/showcase-blocks/${block.id}`, {
        station_opened: f.station_opened, active: f.active,
        onsite_reps: f.onsite_reps.split(",").map((s) => s.trim()).filter(Boolean),
        conversations_count: Number(f.conversations_count) || 0,
        leads_count: Number(f.leads_count) || 0,
        follow_up_requests_count: Number(f.follow_up_requests_count) || 0,
        notes: f.notes || null,
      });
      toast.success("Station activity saved");
      onClose(); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!block} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="showcase-dialog">
        <DialogHeader><DialogTitle>{block?.prime_company} · Station {block?.station_number}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Record showcase station activity for this Prime.</DialogDescription>
        <div className="flex items-center justify-between"><Label className="text-sm">Station opened</Label>
          <Switch checked={f.station_opened} onCheckedChange={(v) => setF({ ...f, station_opened: v })} data-testid="showcase-opened" /></div>
        <div className="flex items-center justify-between"><Label className="text-sm">Active during window</Label>
          <Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="showcase-active" /></div>
        <div><Label className="text-xs">Onsite representatives (comma-separated)</Label>
          <Input value={f.onsite_reps || ""} onChange={(e) => setF({ ...f, onsite_reps: e.target.value })} data-testid="showcase-reps" /></div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs">Conversations</Label><Input type="number" value={f.conversations_count} onChange={(e) => setF({ ...f, conversations_count: e.target.value })} data-testid="showcase-convos" /></div>
          <div><Label className="text-xs">Leads</Label><Input type="number" value={f.leads_count} onChange={(e) => setF({ ...f, leads_count: e.target.value })} data-testid="showcase-leads" /></div>
          <div><Label className="text-xs">Follow-ups</Label><Input type="number" value={f.follow_up_requests_count} onChange={(e) => setF({ ...f, follow_up_requests_count: e.target.value })} data-testid="showcase-followups" /></div>
        </div>
        <div><Label className="text-xs">Notes</Label><Textarea value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="showcase-notes" /></div>
        <DialogFooter>
          <button onClick={save} disabled={busy} className="btn-outline-black h-10 px-5 text-xs" data-testid="showcase-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RelationshipDialog({ rel, stages, closedStatuses, onClose, onSaved }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (rel) setF({
      stage: rel.stage || "", owner: rel.owner || "", responsible_party: rel.responsible_party || "",
      next_action: rel.next_action || "", follow_up_due: rel.follow_up_due || "",
      follow_up_completed_at: rel.follow_up_completed_at || "", closed_reason: rel.closed_reason || "",
      latest_update: rel.latest_update || "", note: "",
    });
  }, [rel]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/program/relationships/${rel.id}`, {
        stage: f.stage || null, owner: f.owner || null, responsible_party: f.responsible_party || null,
        next_action: f.next_action || null, follow_up_due: f.follow_up_due || null,
        follow_up_completed_at: f.follow_up_completed_at || null,
        closed_reason: f.closed_reason || null, latest_update: f.latest_update || null,
        note: f.note || null,
      });
      toast.success("Relationship updated");
      onClose(); onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={!!rel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="rel-dialog">
        <DialogHeader><DialogTitle>{rel?.prime_company} ↔ {rel?.sbe_company}</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Update this Prime–SBE relationship stage, owner and follow-up.</DialogDescription>
        <div><Label className="text-xs">Stage</Label>
          <Select value={f.stage} onValueChange={(v) => setF({ ...f, stage: v })}>
            <SelectTrigger data-testid="rel-stage"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(stages || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              {(closedStatuses || []).map((s) => <SelectItem key={s} value={s}>{s} (closed)</SelectItem>)}
            </SelectContent>
          </Select></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Relationship owner</Label><Input value={f.owner} onChange={(e) => setF({ ...f, owner: e.target.value })} data-testid="rel-owner" /></div>
          <div><Label className="text-xs">Responsible party</Label><Input value={f.responsible_party} onChange={(e) => setF({ ...f, responsible_party: e.target.value })} data-testid="rel-responsible" /></div>
        </div>
        <div><Label className="text-xs">Next action</Label><Input value={f.next_action} onChange={(e) => setF({ ...f, next_action: e.target.value })} data-testid="rel-next-action" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs">Follow-up due</Label><Input type="date" value={f.follow_up_due} onChange={(e) => setF({ ...f, follow_up_due: e.target.value })} data-testid="rel-due" /></div>
          <div><Label className="text-xs">Follow-up completed</Label><Input type="date" value={f.follow_up_completed_at} onChange={(e) => setF({ ...f, follow_up_completed_at: e.target.value })} data-testid="rel-completed" /></div>
        </div>
        <div><Label className="text-xs">Latest update</Label><Textarea value={f.latest_update} onChange={(e) => setF({ ...f, latest_update: e.target.value })} data-testid="rel-latest" /></div>
        <div><Label className="text-xs">Add a note (audit trail)</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="rel-note" /></div>
        <DialogFooter>
          <button onClick={save} disabled={busy} className="btn-outline-black h-10 px-5 text-xs" data-testid="rel-save">Save</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
